/**
 * Company Name Utilities
 * 
 * Functions for processing and displaying company names with suffixes
 */

/**
 * Extracts the company suffix (form type) from a company name
 * Returns an object with the main name and the suffix
 */
export function extractCompanySuffix(name: string): { mainName: string; suffix: string } {
  if (!name || typeof name !== 'string') {
    return { mainName: name || '', suffix: '' };
  }

  const trimmed = name.trim();
  
  // Common company form suffixes (Finnish, Swedish, International)
  // Order matters: longer patterns first to avoid partial matches
  const companyFormPatterns = [
    // Full forms (longest first)
    /\s+(Julkinen osakeyhtiö|Kommandiittiyhtiö|Osakeyhtiö|Toiminimi)\s*$/i,
    /\s+(Publikt aktiebolag|Kommanditbolag|Handelsbolag|Aktiebolag|Företagsnamn)\s*$/i,
    // Combined forms with multiple suffixes (e.g., "Corporation Ab", "Corporation Ltd")
    /\s+(Corporation|Incorporated|Limited)\s+(Ab\.|Ab|Ltd\.|Ltd|Inc\.|Inc|LLC|L\.L\.C\.|L\.L\.C|Oy|Oyj|Plc|Plc\.)\s*$/i,
    // Combined Finnish/Swedish forms
    /\s+(Oy Ab\.|Oy Ab|Oyj Abp|Oyj Ab\.|Publ AB\.|Publ AB|Publ\. AB\.|Publ\. AB)\s*$/i,
    // Individual abbreviations
    /\s+(Oyj|Tmi\.|Tmi|Ky\.|Ky|Osk\.|Osk|Oy|KB\.|KB|HB\.|HB|Ab\.|Ab|Abp\.|Abp)\s*$/i,
    /\s+(Incorporated|Corporation|Limited|L\.L\.C\.|L\.L\.C|LLC|Ltd\.|Ltd|Inc\.|Inc|Corp\.|Corp|Plc|Plc\.)\s*$/i,
    // Country names as company form suffixes
    /\s+(Suomi|Finland)\s*$/i,
    // Also match without word boundary for abbreviations
    /\b(oy|ab|ltd|oyj|oy ab|oyj abp|abp|osakeyhtiö|aktiebolag|inc|llc|plc|ky|kb|ay|tmi|toiminimi|aktiebolaget|aktiebolag ab|aktiebolaget ab|aktiebolag oy|aktiebolaget oy|aktiebolag ltd|aktiebolaget ltd|aktiebolag oyj|aktiebolaget oyj|aktiebolag ky|aktiebolaget ky|aktiebolag kb|aktiebolaget kb|aktiebolag ay|aktiebolaget ay|aktiebolag tmi|aktiebolaget tmi|suomi|finland)\.?$/i,
  ];

  for (const pattern of companyFormPatterns) {
    const match = trimmed.match(pattern);
    if (match) {
      const suffix = match[0].trim();
      const mainName = trimmed.replace(pattern, '').trim();
      // Capitalise 'Kaupunki' if present (case-insensitive)
      const cleanedMainName = mainName.replace(/kaupunki/gi, (m) => 
        m.charAt(0).toUpperCase() + m.slice(1).toLowerCase()
      );
      return { mainName: cleanedMainName, suffix };
    }
  }

  // No suffix found, return the name as-is (with Kaupunki capitalization)
  const cleaned = trimmed.replace(/kaupunki/gi, (match) => 
    match.charAt(0).toUpperCase() + match.slice(1).toLowerCase()
  );
  return { mainName: cleaned, suffix: '' };
}

/**
 * Properly capitalizes a company name to title case.
 * Capitalizes the first letter of each word, lowercases the rest.
 * Handles special cases like "Kaupunki" and preserves existing capitalization patterns.
 */
export function capitalizeCompanyName(name: string): string {
  if (!name || typeof name !== 'string') return name || '';
  
  return name
    .trim()
    .split(/\s+/)
    .map((word) => {
      if (!word) return word;
      // Capitalize first letter, lowercase the rest
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
}

/**
 * Strips company type suffixes from a company name (legacy function for backward compatibility)
 * @deprecated Use extractCompanySuffix instead for display purposes
 */
export function stripCompanyType(name: string): string {
  const { mainName } = extractCompanySuffix(name);
  return mainName;
}
